<template>
    <div class="py-12">
        <div class="flex flex-wrap justify-between">
            <h1 class="page-header text-2xl font-bold text-gray-800">
                {{ $t("vue.domains") }}
            </h1>

            <AtomicButton
                v-if="domains.length > 0"
                color="yellow"
                size="lg"
                @click="handleAddDomain"
            >
                <template #icon>
                    <PlusCircleIcon class="mr-2 h-6 w-6" />
                </template>
                {{ t("vue.new_domain") }}
            </AtomicButton>
        </div>
    </div>

    <div class="flex flex-wrap justify-between">
        <DomainsToolbar index-route="domains.index" />
    </div>

    <div v-if="domains.length === 0" class="mt-6">
        <div class="flex flex-wrap justify-between">
            <div class="domains-none-list">
                <img
                    :src="domainsNoneImgSrc"
                    alt="Domains none image"
                    class="domains-none-image"
                />

                <div class="domains-none-text">
                    <h2 class="domain-non-text-header">
                        {{ t("vue.none_domains_yet") }}
                    </h2>
                    <p class="domain-non-text-header-paragraph">
                        {{ t("vue.none_domains_yet_instruction") }}
                    </p>
                </div>

                <div class="domains-none-button">
                    <AtomicButton
                        color="yellow"
                        size="lg"
                        @click="handleAddDomain"
                    >
                        <template #icon>
                            <PlusCircleIcon class="mr-2 h-6 w-6" />
                        </template>
                        {{ t("vue.new_domain") }}
                    </AtomicButton>
                </div>
            </div>
        </div>
    </div>

    <div v-else-if="domains.length > 0" class="mt-6">
        <div class="domains-table-block mt-6">
            <DomainsOverview
                :total-domains="totalDomainsCount"
                :total-active-domains="totalActiveDomains"
                :total-inactive-domains="totalInactiveDomains"
                :empty-domains-slots="emptyDomainsSlots"
                :total-emails-reported="totalEmailsReported"
            />

            <div class="domains-table-content-block">
                <table
                    class="w-full border-collapse bg-white rounded-lg relative"
                >
                    <thead>
                        <tr>
                            <th
                                class="px-6 domain-table-head-td py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >
                                {{ t("vue.dashboard_table_domain") }}
                            </th>
                            <th
                                class="px-6 domain-table-head-td py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >
                                {{ t("vue.status") }}
                            </th>
                            <th
                                class="px-6 domain-table-head-td py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >
                                {{ t("vue.created_at") }}
                            </th>
                            <th
                                class="px-6 domain-table-head-td py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >
                                {{ t("vue.dmarc_policy") }}
                            </th>
                            <!--                            <th-->
                            <!--                                class="px-6 domain-table-head-td py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"-->
                            <!--                            >-->
                            <!--                                {{ t("vue.emails") }} ({{-->
                            <!--                                    t("vue.last_30_days")-->
                            <!--                                }})-->
                            <!--                            </th>-->
                            <th class="px-6 domain-table-head-td py-3"></th>
                        </tr>
                    </thead>

                    <tbody>
                        <Domain
                            v-for="(domain, index) in domains"
                            :key="index"
                            :domain="domain"
                            @remove="removeDomain"
                            @refreshDomains="refreshDomains"
                            @validate="validateDomain"
                        />
                    </tbody>
                </table>

                <div class="mt-4 flex items-center justify-between px-2">
                    <p class="text-sm text-gray-700">
                        Showing
                        <span class="font-medium">{{
                            pagination.from ?? (domains.length ? 1 : 0)
                        }}</span>
                        to
                        <span class="font-medium">{{
                            pagination.to ?? domains.length
                        }}</span>
                        of
                        <span class="font-medium">{{
                            pagination.total ?? domains.length
                        }}</span>
                        results
                    </p>

                    <nav
                        class="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1"
                        aria-label="Pagination"
                    >
                        <button
                            class="rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                            :disabled="pagination.current_page <= 1"
                            @click="goToPage(pagination.current_page - 1)"
                        >
                            ‹
                        </button>

                        <button
                            v-for="page in visiblePages"
                            :key="page"
                            :disabled="page === '...'"
                            class="min-w-9 rounded-md px-3 py-2 text-sm"
                            :class="
                                page === pagination.current_page
                                    ? 'bg-gray-100 font-semibold text-gray-900'
                                    : page === '...'
                                      ? 'text-gray-700'
                                      : 'text-gray-700 hover:bg-gray-50'
                            "
                            @click="page !== '...' && goToPage(page as number)"
                        >
                            {{ page }}
                        </button>

                        <button
                            class="rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                            :disabled="
                                pagination.current_page >= pagination.last_page
                            "
                            @click="goToPage(pagination.current_page + 1)"
                        >
                            ›
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    </div>

    <CreateDomainModal
        v-if="createDomainModal"
        @close="closeCreateDomainModal"
        @created="onDomainCreated"
    />

    <DomainLimitModal
        :show="showDomainLimitModal"
        :message="domainLimitMessage"
        @close="closeDomainLimitModal"
    />
</template>

<script lang="ts" setup>
import { type PropType, computed, ref, onMounted } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    PlusCircleIcon,
} from "@heroicons/vue/24/outline";
import type IDomain from "@/types/Domains/IDomain";
import { useTranslations } from "@/composables/useTranslations";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import DomainLimitModal from "@/Pages/Domains/components/DomainLimitModal.vue";
import Domain from "@/Pages/Domains/components/Domain.vue";
import CreateDomainModal from "@/Pages/Domains/components/CreateDomainModal.vue";
import axios from "axios";
import { create } from "@/composables/useNotifications";
import useConfirm from "@/composables/useConfirm";
import DomainsToolbar from "@/Pages/Domains/components/DomainsToolbar.vue";
import DomainsOverview from "@/Pages/Domains/components/DomainsOverview.vue";
const domainsNoneImgSrc = new URL(
    "/resources/js/assets/images/dmarceye-app-img-domains-none.png",
    import.meta.url,
).href;

// Import composables
const { show } = useConfirm();

// Get access to translations
const { t } = useTranslations();

const props = defineProps({
    domains: {
        type: Array as PropType<IDomain[]>,
        default: () => [],
    },
    activeDomains: {
        type: Array as PropType<IDomain[]>,
        default: () => [],
    },
    inactiveDomains: {
        type: [Array, Object] as PropType<IDomain[] | Record<string, IDomain>>,
        default: () => [],
    },
    totalInactiveDomains: {
        type: Number,
        default: 0,
    },
    emptyDomainsSlots: {
        type: Number,
        default: 0,
    },
    totalEmailsReported: {
        type: Number,
        default: 0,
    },
    totalDomainsCount: {
        type: Number,
        default: 0,
    },
    totalActiveDomains: {
        type: Number,
        default: 0,
    },
    pagination: {
        type: Object as PropType<{
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
            from: number | null;
            to: number | null;
        }>,
        default: () => ({
            current_page: 1,
            last_page: 1,
            per_page: 20,
            total: 0,
            from: 0,
            to: 0,
        }),
    },
});

// Get access to global props
const page = usePage<IRhizomPageProps>();

const canAddDomain = computed(() => {
    const hasBasicPlan = page.props.hasBasicPlan;
    const hasScalePlan = page.props.hasScalePlan;
    const domainCount = page.props.totalDomainsCount;
    const hasGodMode = page.props.hasGodMode;

    if (hasGodMode) {
        return true;
    }

    if (hasBasicPlan && !hasScalePlan) {
        return domainCount < 1;
    }

    if (hasScalePlan) {
        return domainCount < 50;
    }

    return page.props.isTrialing;
});

const createDomainModal = ref(false);
const validationLoading = ref(false);
const showDomainLimitModal = ref(false);
const domainLimitMessage = ref("");

// Chunking state
const currentPage = ref(1);
const totalPages = ref(1);
const chunkSize = ref(10);
const totalDomains = ref(0);
const processedDomains = ref(0);

const inactiveDomainsArray = computed(() => {
    if (Array.isArray(props.inactiveDomains)) {
        return props.inactiveDomains;
    }
    return Object.values(props.inactiveDomains || {});
});

const inactiveDomainsEnhanced = ref<IDomain[]>([]);

const closeCreateDomainModal = () => {
    createDomainModal.value = false;
    // Refresh domains when modal is closed
    router.visit(route("domains.index"), {
        method: "get",
        preserveState: false,
        preserveScroll: false,
    });
};

const handleAddDomain = () => {
    if (canAddDomain.value) {
        createDomainModal.value = true;
    } else {
        // Show domain limit modal with appropriate message
        const hasBasicPlan = page.props.hasBasicPlan;
        const hasScalePlan = page.props.hasScalePlan;
        const isTrialing = page.props.isTrialing;
        const hasGodMode = page.props.hasGodMode;

        if (hasGodMode) {
            return;
        }

        if (hasBasicPlan && !hasScalePlan) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_basic_plan",
            );
        } else if (hasScalePlan) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_scale_plan",
            );
        } else if (!isTrialing) {
            domainLimitMessage.value = t(
                "vue.cannot_add_more_domains_upgrade_required",
            );
        }

        showDomainLimitModal.value = true;
    }
};

const closeDomainLimitModal = () => {
    showDomainLimitModal.value = false;
    domainLimitMessage.value = "";
};

const removeDomain = async (domain: string) => {
    const confirmed = await show({
        header: t("vue.delete_domain"),
        message: t("vue.are_you_sure_delete_domain"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed) {
        router.delete(route("domains.destroy", { domain: domain }), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                create({
                    title: t("vue.domain_removed"),
                    text: t("vue.domain_removed_successfully"),
                    type: "success",
                });
            },
            onError: (e) => {
                create({
                    title: t("vue.error"),
                    text: e.message || t("vue.error_removing_domain"),
                    type: "error",
                });
            },
        });
    }
};

const refreshDomains = () => {
    router.reload({ only: ["domains", "activeDomains", "inactiveDomains"] });
};

const validateDomain = async (domain: string) => {
    validationLoading.value = true;

    try {
        const result = await axios.post(
            route("domains.validate.store", { domain: domain }),
        );

        if (result.status === 204) {
            create({
                title: t("vue.domain_validated"),
                text: t("vue.domain_validated_successfully"),
                type: "success",
            });

            router.reload({ only: ["activeDomains", "inactiveDomains"] });
        } else {
            create({
                title: t("vue.domain_not_validated"),
                text: t("vue.could_not_validate_domain"),
                type: "warning",
            });
        }
    } catch (e) {
        () => undefined;
    } finally {
        validationLoading.value = false;
    }
};

// Initialize enhanced domains on mount
onMounted(() => {
    // Initialize with current domains
    inactiveDomainsEnhanced.value = [...inactiveDomainsArray.value];

    // Initialize total domains count
    totalDomains.value = inactiveDomainsArray.value.length;
});

const pagination = computed(() => props.pagination);

const visiblePages = computed<(number | string)[]>(() => {
    const cur = pagination.value.current_page;
    const last = pagination.value.last_page;

    const window = 1;
    const pages: (number | string)[] = [];

    pages.push(1);

    if (cur - window > 2) pages.push("...");

    const start = Math.max(2, cur - window);
    const end = Math.min(last - 1, cur + window);
    for (let p = start; p <= end; p++) pages.push(p);

    if (cur + window < last - 1) pages.push("...");

    if (last > 1) pages.push(last);

    return pages.filter((v, i, a) => i === 0 || v !== a[i - 1]);
});

const goToPage = (page: number) => {
    if (!page || page < 1 || page > pagination.value.last_page) return;

    const params = new URLSearchParams(window.location.search);
    params.set("page", String(page));

    router.get(route("domains.index"), Object.fromEntries(params), {
        preserveState: true,
        preserveScroll: true,
    });
};
</script>

<style scoped>
/*DOMAINS NONE LIST*/
.domains-none-list {
    display: flex;
    width: 539px;
    flex-direction: column;
    align-items: center;
    margin: auto;
    gap: 41px;
}

.domains-none-image {
    width: 539px;
    height: 244px;
    aspect-ratio: 539/244;
}

.domains-none-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 9px;
}

.domains-none-button {
    display: flex;
    width: 135px;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.page-header {
    color: #252f3f;
    font-style: normal;
    font-weight: 700;
    line-height: 140%; /* 35.28px */
}

.domain-non-text-header {
    color: #000;
    text-align: center;
    font-size: 35px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%; /* 52.8px */
}

.domain-non-text-header-paragraph {
    color: #18181b;
    text-align: center;
    font-size: 16px;
    font-style: normal;
    width: 539px;
    font-weight: 400;
    line-height: 29px; /* 181.25% */
    letter-spacing: -0.18px;
}

/*DOMAINS CONTENT BLOCK*/
.domains-table-block {
    max-width: 1330px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 30px;
}

.domains-table-content-block {
    padding: 0.75rem 0.5rem;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    background: #fff;
    box-shadow:
        0 1px 1px rgba(16, 24, 40, 0.04),
        0 1px 2px rgba(16, 24, 40, 0.06);
    overflow: visible;
    margin-bottom: 250px;
}

table,
thead,
tbody,
tr,
td,
th {
    overflow: visible;
}

thead .domain-table-row > :nth-child(6) {
    justify-content: flex-end;
    text-align: right;
}

thead .domain-table-row > :nth-child(6) {
    justify-content: flex-end;
}

.domain-table-head-td {
    display: table-cell;
    padding: 12px 20px;
    height: 44px;
    border-bottom: 1px solid #e5e5e5;
    background: #fff;
    color: #737373;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 20px;
    letter-spacing: 0.02em;
    white-space: nowrap;
}
</style>
